import {z as zod} from "zod";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import type {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";

export const configurationSchemaFieldValidator: zod.ZodType<ConfigurationSchemaField> = zod.lazy(() =>
    zod.object({
        name: zod.string(),
        optional: zod.boolean(),
        type: zod.string(),
        fields: zod.array(configurationSchemaFieldValidator).optional(),
    }),
);

export type ConfigurationSchemaField = {
    name: string;
    optional: boolean;
    type: string;
    fields?: Array<ConfigurationSchemaField>;
};

export const workspaceCapabilityDefinitionMetadataValidator = zod.object({
    type: uuidZodValidator,
    displayName: zod.string(),
    id: uuidZodValidator,
    configurationSchema: zod.array(configurationSchemaFieldValidator),
    dependencies: zod.object({
        workspaceCapabilities: zod.array(uuidZodValidator),
    }),
});

export const workspaceDefinitionsMetadataValidator = zod.object({
    capabilities: zod.array(workspaceCapabilityDefinitionMetadataValidator),
});

export type WorkspaceCapabilityDefinitionMetadata = zod.infer<typeof workspaceCapabilityDefinitionMetadataValidator> & {
    type: WorkspaceCapabilityType;
    dependencies: {
        workspaceCapabilities: Array<WorkspaceCapabilityType>;
    };
};

export type WorkspaceDefinitionsMetadata = {
    capabilities: Array<WorkspaceCapabilityDefinitionMetadata>;
};

export function parseWorkspaceDefinitionsMetadata(
    data: zod.infer<typeof workspaceDefinitionsMetadataValidator>,
): WorkspaceDefinitionsMetadata {
    return data as WorkspaceDefinitionsMetadata;
}

export function buildWorkspaceDefinitionMetadataLookups(metadata: WorkspaceDefinitionsMetadata): {
    capabilityByType: Map<WorkspaceCapabilityType, WorkspaceCapabilityDefinitionMetadata>;
    capabilityDisplayNameByType: Map<WorkspaceCapabilityType, string>;
} {
    const capabilityByType = new Map<WorkspaceCapabilityType, WorkspaceCapabilityDefinitionMetadata>();
    for (const capability of metadata.capabilities) {
        capabilityByType.set(capability.type, capability);
    }

    return {
        capabilityByType,
        capabilityDisplayNameByType: new Map(metadata.capabilities.map((capability) => [capability.type, capability.displayName])),
    };
}

export function getWorkspaceCapabilityDisplayName(
    type: WorkspaceCapabilityType,
    capabilityDisplayNameByType: Map<WorkspaceCapabilityType, string>,
): string {
    return capabilityDisplayNameByType.get(type) ?? type;
}

export function formatCapabilityDependenciesPreview(
    dependencies: ReadonlyArray<WorkspaceCapabilityType>,
    capabilityDisplayNameByType: Map<WorkspaceCapabilityType, string>,
): string {
    if (dependencies.length === 0) {
        return "None";
    }
    return dependencies.map((dependency) => getWorkspaceCapabilityDisplayName(dependency, capabilityDisplayNameByType)).join(", ");
}

function formatConfigurationSchemaField(field: ConfigurationSchemaField, prefix = ""): string {
    const fieldLabel = prefix === "" ? field.name : `${prefix}.${field.name}`;
    const requirementLabel = field.optional === true ? "optional" : "required";
    if (field.fields !== undefined && field.fields.length > 0) {
        return field.fields.map((nestedField) => formatConfigurationSchemaField(nestedField, fieldLabel)).join("; ");
    }
    return `${fieldLabel} (${field.type}, ${requirementLabel})`;
}

export function formatConfigurationSchemaPreview(fields: ReadonlyArray<ConfigurationSchemaField>): string {
    if (fields.length === 0) {
        return "None";
    }
    return fields.map((field) => formatConfigurationSchemaField(field)).join("; ");
}
